<template>
  <div ref="rootRef" class="auth-modal__area-select">
    <button
      ref="triggerRef"
      type="button"
      class="auth-modal__area-select-trigger"
      :aria-label="t('auth.areaCodeLabel')"
      :aria-expanded="open"
      @click.stop="toggleOpen"
    >
      <span class="auth-modal__area-select-value">{{ modelValue }}</span>
      <svg
        class="auth-modal__area-select-arrow"
        :class="{ 'auth-modal__area-select-arrow--open': open }"
        viewBox="0 0 8 5"
        width="8"
        height="5"
        aria-hidden="true"
      >
        <path d="M1 1l3 3 3-3" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
      </svg>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        ref="panelRef"
        class="auth-modal__area-select-panel"
        :style="panelStyle"
        role="listbox"
        :aria-label="t('auth.areaCodeLabel')"
        @click.stop
      >
        <div class="auth-modal__area-select-search-wrap">
          <input
            v-model="searchQuery"
            type="search"
            class="auth-modal__area-select-search"
            :placeholder="t('auth.areaCodeSearchPlaceholder')"
            autocomplete="off"
          />
        </div>
        <div class="auth-modal__area-select-scroll">
          <p v-if="loading" class="auth-modal__area-select-empty">{{ t('auth.areaCodeLoading') }}</p>
          <template v-else-if="groupedList.length">
            <section
              v-for="group in groupedList"
              :key="group.title || 'all'"
              class="auth-modal__area-select-group"
            >
              <p v-if="group.title" class="auth-modal__area-select-group-title">{{ group.title }}</p>
              <button
                v-for="item in group.items"
                :key="`${item.countryCode}-${item.areaCode}`"
                type="button"
                class="auth-modal__area-select-item"
                :class="{ 'auth-modal__area-select-item--active': item.areaCode === modelValue }"
                role="option"
                :aria-selected="item.areaCode === modelValue"
                @click="selectItem(item)"
              >
                <span class="auth-modal__area-select-item-name">{{ regionLabel(item) }}</span>
                <span class="auth-modal__area-select-item-code">{{ item.areaCode }}</span>
              </button>
            </section>
          </template>
          <p v-else class="auth-modal__area-select-empty">{{ t('auth.noAreaCodeResults') }}</p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useLandingLocale, type LandingLocale } from '@/composables/useLandingLocale'
import { FALLBACK_AREA_CODES, getAreaCodeList, type AreaCodeItem } from '@/services/areaCodeApi'

const COMMON_COUNTRY_CODES = ['CHN', 'HKG', 'MAC', 'TWN', 'USA', 'GBR', 'SGP']
const PANEL_WIDTH = 280
const PANEL_GAP = 4

const props = defineProps<{
  modelValue: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string]
  change: [value: string]
}>()

const { t, locale } = useLandingLocale()

const rootRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLButtonElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
const open = ref(false)
const loading = ref(false)
const searchQuery = ref('')
const areaCodeList = ref<AreaCodeItem[]>([...FALLBACK_AREA_CODES])
const panelStyle = ref<Record<string, string>>({})

function regionLabel(item: AreaCodeItem): string {
  const loc = locale.value as LandingLocale
  if (loc === 'en') return item.en
  if (loc === 'zh-TW') return item.zhTw
  return item.zhCn
}

function normalizeSearchText(value: string): string {
  return value.trim().toLowerCase()
}

const filteredList = computed(() => {
  const query = normalizeSearchText(searchQuery.value)
  if (!query) return areaCodeList.value
  return areaCodeList.value.filter((item) => {
    const label = regionLabel(item).toLowerCase()
    return (
      label.includes(query) ||
      item.en.toLowerCase().includes(query) ||
      item.zhCn.includes(searchQuery.value.trim()) ||
      item.zhTw.includes(searchQuery.value.trim()) ||
      item.areaCode.includes(searchQuery.value.trim()) ||
      (item.initial ?? '').toLowerCase().includes(query)
    )
  })
})

const groupedList = computed(() => {
  const list = filteredList.value
  if (!list.length) return []

  const query = searchQuery.value.trim()
  if (query) {
    return [{ title: '', items: list }]
  }

  const commonItems: AreaCodeItem[] = []
  const letterMap = new Map<string, AreaCodeItem[]>()

  list.forEach((item) => {
    if (COMMON_COUNTRY_CODES.includes(item.countryCode)) {
      commonItems.push(item)
      return
    }
    const letter = (item.countryCode?.slice(0, 1) || '#').toUpperCase()
    const bucket = letterMap.get(letter) ?? []
    bucket.push(item)
    letterMap.set(letter, bucket)
  })

  const groups: { title: string; items: AreaCodeItem[] }[] = []
  if (commonItems.length) {
    groups.push({ title: t('auth.commonRegions'), items: commonItems })
  }
  Array.from(letterMap.keys())
    .sort()
    .forEach((letter) => {
      groups.push({ title: letter, items: letterMap.get(letter) ?? [] })
    })
  return groups
})

function updatePanelPosition() {
  const trigger = triggerRef.value
  if (!trigger || typeof window === 'undefined') return

  const rect = trigger.getBoundingClientRect()
  const viewportWidth = window.innerWidth
  const maxLeft = viewportWidth - PANEL_WIDTH - 12
  const left = Math.min(rect.left, Math.max(12, maxLeft))

  panelStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + PANEL_GAP}px`,
    left: `${left}px`,
    width: `${PANEL_WIDTH}px`,
    zIndex: '600',
  }
}

async function syncPanelPosition() {
  await nextTick()
  if (typeof window === 'undefined') return
  window.requestAnimationFrame(() => {
    updatePanelPosition()
  })
}

async function loadAreaCodeList() {
  if (loading.value) return
  loading.value = true
  try {
    areaCodeList.value = await getAreaCodeList()
  } finally {
    loading.value = false
    if (open.value) {
      await syncPanelPosition()
    }
  }
}

async function toggleOpen() {
  open.value = !open.value
  if (open.value) {
    searchQuery.value = ''
    await syncPanelPosition()
    void loadAreaCodeList()
  }
}

function selectItem(item: AreaCodeItem) {
  if (item.areaCode !== props.modelValue) {
    emit('update:modelValue', item.areaCode)
    emit('change', item.areaCode)
  }
  open.value = false
  searchQuery.value = ''
}

function onDocumentClick(event: MouseEvent) {
  if (!open.value) return
  const root = rootRef.value
  const panel = panelRef.value
  const target = event.target as Node
  if (root?.contains(target) || panel?.contains(target)) return
  open.value = false
}

function onViewportChange() {
  if (open.value) updatePanelPosition()
}

watch(open, async (visible) => {
  if (typeof document === 'undefined') return
  if (visible) {
    await syncPanelPosition()
    document.addEventListener('click', onDocumentClick)
    window.addEventListener('resize', onViewportChange)
    window.addEventListener('scroll', onViewportChange, true)
  } else {
    document.removeEventListener('click', onDocumentClick)
    window.removeEventListener('resize', onViewportChange)
    window.removeEventListener('scroll', onViewportChange, true)
  }
})

onMounted(() => {
  void loadAreaCodeList()
})

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick)
  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('scroll', onViewportChange, true)
})
</script>

<style scoped>
.auth-modal__area-select {
  position: relative;
  flex: 0 0 80px;
  width: 80px;
  min-width: 80px;
  max-width: 80px;
}

.auth-modal__area-select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  height: 52px;
  padding: 0 8px 0 12px;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  color: var(--ink, #181818);
  background: transparent;
  border: none;
  cursor: pointer;
}

.auth-modal__area-select-value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.auth-modal__area-select-arrow {
  flex-shrink: 0;
  margin-left: 4px;
  color: #9c9c9c;
  transition: transform 150ms ease;
}

.auth-modal__area-select-arrow--open {
  transform: rotate(180deg);
}

.auth-modal__area-select-panel {
  box-sizing: border-box;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  font-size: 14px;
  line-height: normal;
  color: #181818;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.auth-modal__area-select-search-wrap {
  padding: 8px 10px;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.auth-modal__area-select-search {
  box-sizing: border-box;
  width: 100%;
  height: 32px;
  padding: 0 12px;
  font-size: 13px;
  line-height: normal;
  color: #181818;
  background: #f7f7f7;
  border: none;
  border-radius: 16px;
  outline: none;
}

.auth-modal__area-select-search::placeholder {
  color: #9c9c9c;
}

.auth-modal__area-select-scroll {
  max-height: 240px;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 4px 8px 8px;
}

.auth-modal__area-select-group-title {
  margin: 0;
  padding: 8px 8px 4px;
  color: #9c9c9c;
  font-size: 12px;
  line-height: 16px;
}

.auth-modal__area-select-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: 100%;
  min-height: 32px;
  padding: 6px 8px;
  color: #333;
  font-size: 13px;
  line-height: normal;
  text-align: left;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.auth-modal__area-select-item:hover,
.auth-modal__area-select-item--active {
  color: #ff6907;
  background: #f3f4f6;
}

.auth-modal__area-select-item-name {
  flex: 1;
  min-width: 0;
  margin-right: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.auth-modal__area-select-item-code {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 500;
}

.auth-modal__area-select-empty {
  margin: 0;
  padding: 20px 8px;
  color: #666;
  font-size: 13px;
  line-height: normal;
  text-align: center;
}

@media (max-width: 720px) {
  .auth-modal__area-select-trigger {
    height: 40px;
    font-size: 13px;
  }

  .auth-modal__area-select-item {
    font-size: 12px;
  }

  .auth-modal__area-select-item-code {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .auth-modal__area-select-trigger {
    height: 38px;
    font-size: 12px;
  }
}
</style>
